/**
 * Super Admin Chat Page
 *
 * Isolated chat for super admins. Scope is always super_admin, no municipalityId.
 */

import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { Row, Col, message as antdMessage } from 'antd';
import styled from '@emotion/styled';
import { useAuth } from '../hooks/useAuth';
import {
  useChatStore,
  useRealTimeConversations,
  useRealTimeMessages,
  usePresence,
  useTypingIndicator,
  useBrowserNotifications,
} from '@municipality/chat';
import type { Message } from '@municipality/types';
import { ConversationList } from '../components/chat/ConversationList';
import { ChatView } from '../components/chat/ChatView';
import { NewChatModal } from '../components/chat/NewChatModal';
import { AdvancedSearchModal } from '../components/chat/AdvancedSearchModal';
import { useResponsive } from '@municipality/ui';

const SCOPE = 'super_admin' as const;
const MUNICIPALITY_ID = undefined;

const ChatContainer = styled.div<{ $isTablet: boolean; $isMobile: boolean }>`
  height: ${({ $isTablet, $isMobile }) => {
    if ($isMobile) return 'calc(100vh - 64px - 20px)';
    if ($isTablet) return 'calc(100vh - 64px - 20px)';
    return 'calc(100vh - 200px)';
  }};
  background-color: #fff;
  border-radius: ${({ $isTablet, $isMobile }) => ($isTablet || $isMobile ? '0' : '8px')};
  overflow: hidden;
  box-shadow: ${({ $isTablet, $isMobile }) => ($isTablet || $isMobile ? 'none' : '0 2px 8px rgba(0, 0, 0, 0.05)')};
  ${({ $isMobile }) =>
    $isMobile &&
    `
    padding-bottom: env(safe-area-inset-bottom);
  `}
`;

const Chat: React.FC = () => {
  const { user } = useAuth();
  const { isTablet, isMobile } = useResponsive();
  const [newChatModalVisible, setNewChatModalVisible] = useState(false);
  const [searchModalVisible, setSearchModalVisible] = useState(false);

  const {
    conversations,
    activeConversationId,
    messages,
    isLoading,
    setActiveConversation,
    sendMessage,
    createDirectConversation,
    createGroupConversation,
    muteConversation,
    archiveConversation,
    pinConversation,
  } = useChatStore();

  const activeConversation = useMemo(
    () => conversations.find((conv) => conv.id === activeConversationId) ?? null,
    [conversations, activeConversationId]
  );

  const activeMessages = activeConversationId ? (messages[activeConversationId] ?? []) : [];

  useRealTimeConversations(user?.uid, SCOPE, MUNICIPALITY_ID);
  useRealTimeMessages(activeConversationId, user?.uid);
  usePresence(user?.uid, SCOPE, MUNICIPALITY_ID);

  const { setTyping } = useTypingIndicator(activeConversationId);
  const { showNotification } = useBrowserNotifications(user?.uid, { chatPath: '/chat' });

  const previousLastMessagesRef = useRef<Map<string, { senderId: string; timestamp: Date }>>(new Map());
  const isInitializedRef = useRef(false);

  useEffect(() => {
    useChatStore.getState().setScope(SCOPE, MUNICIPALITY_ID);
  }, []);

  useEffect(() => {
    if (!user?.uid || isInitializedRef.current) return;
    if (conversations.length > 0) {
      conversations.forEach((conversation) => {
        if (conversation.lastMessage) {
          previousLastMessagesRef.current.set(conversation.id, {
            senderId: conversation.lastMessage.senderId,
            timestamp: conversation.lastMessage.timestamp,
          });
        }
      });
      isInitializedRef.current = true;
    }
  }, [conversations, user?.uid]);

  useEffect(() => {
    if (!user?.uid || !isInitializedRef.current) return;
    conversations.forEach((conversation) => {
      if (!conversation.lastMessage) return;
      const previousLastMessage = previousLastMessagesRef.current.get(conversation.id);
      const currentLastMessage = {
        senderId: conversation.lastMessage.senderId,
        timestamp: conversation.lastMessage.timestamp,
      };
      const isNewMessage =
        !previousLastMessage ||
        previousLastMessage.timestamp.getTime() !== currentLastMessage.timestamp.getTime() ||
        previousLastMessage.senderId !== currentLastMessage.senderId;

      if (isNewMessage && currentLastMessage.senderId !== user.uid) {
        const notificationMessage: Message = {
          id: `notification-${conversation.id}-${currentLastMessage.timestamp.getTime()}`,
          conversationId: conversation.id,
          senderId: currentLastMessage.senderId,
          senderName: conversation.lastMessage.senderName,
          content: conversation.lastMessage.content,
          type: 'text' as const,
          createdAt: currentLastMessage.timestamp,
          isEdited: false,
          deletedBy: [],
          readBy: {},
          deliveredTo: {},
          reactions: {},
          expiresAt: new Date(Date.now() + 2 * 365 * 24 * 60 * 60 * 1000),
        };
        showNotification(notificationMessage, conversation);
      }
      previousLastMessagesRef.current.set(conversation.id, currentLastMessage);
    });
  }, [conversations, user?.uid, showNotification]);

  const handleSelectConversation = useCallback(
    (conversationId: string) => {
      setActiveConversation(conversationId);
    },
    [setActiveConversation]
  );

  const handleBackToConversations = useCallback(() => {
    setActiveConversation(null);
  }, [setActiveConversation]);

  const handleJumpToMessage = useCallback(
    (conversationId: string, messageId: string, messageDate?: Date) => {
      setActiveConversation(conversationId);
      setTimeout(() => {
        window.dispatchEvent(
          new globalThis.CustomEvent('jumpToMessage', { detail: { messageId, messageDate } })
        );
      }, 300);
    },
    [setActiveConversation]
  );

  useEffect(() => {
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'f' && (event.ctrlKey || event.metaKey)) {
        const target = event.target as globalThis.HTMLElement;
        if (target.tagName !== 'INPUT' && target.tagName !== 'TEXTAREA') {
          event.preventDefault();
          setSearchModalVisible(true);
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown as never);
    return () => document.removeEventListener('keydown', handleKeyDown as never);
  }, []);

  useEffect(() => {
    const handleSelect = (e: globalThis.Event) => {
      const customEvent = e as globalThis.CustomEvent<{ conversationId: string }>;
      if (customEvent.detail?.conversationId) {
        handleSelectConversation(customEvent.detail.conversationId);
      }
    };
    window.addEventListener('selectConversation', handleSelect);
    return () => window.removeEventListener('selectConversation', handleSelect);
  }, [handleSelectConversation]);

  const handleSendMessage = async (content: string, mentions?: string[], replyTo?: string) => {
    if (!activeConversationId || !user) return;
    try {
      setTyping(user.uid, false).catch(() => {});
      const metadata = {
        ...(mentions && mentions.length > 0 ? { mentions } : {}),
        ...(replyTo ? { replyTo } : {}),
      };
      const messageMetadata = Object.keys(metadata).length > 0 ? metadata : undefined;
      await sendMessage(
        activeConversationId,
        content,
        user.uid,
        (user as { displayName?: string }).displayName ?? user.email ?? 'User',
        'text',
        messageMetadata
      );
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Failed to send message';
      antdMessage.error(msg.includes('Rate limit') ? msg : 'Failed to send message');
    }
  };

  const handleTyping = async (isTyping: boolean) => {
    if (!user) return;
    await setTyping(user.uid, isTyping);
  };

  const handleCreateDirectChat = async (recipientId: string) => {
    if (!user) return;
    try {
      const conversation = await createDirectConversation(
        user.uid,
        recipientId,
        SCOPE,
        MUNICIPALITY_ID
      );
      if (conversation) {
        setActiveConversation(conversation.id);
        antdMessage.success('Conversation created');
      }
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Failed';
      antdMessage.error(msg.includes('Rate limit') ? msg : 'Failed to create conversation');
    }
  };

  const handleCreateGroupChat = async (name: string, participantIds: string[]) => {
    if (!user) return;
    try {
      const allParticipants = [...participantIds, user.uid];
      const conversation = await createGroupConversation(
        name,
        allParticipants,
        user.uid,
        SCOPE,
        MUNICIPALITY_ID
      );
      if (conversation) {
        setActiveConversation(conversation.id);
        antdMessage.success('Group chat created');
      }
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Failed';
      antdMessage.error(msg.includes('Rate limit') ? msg : 'Failed to create group chat');
    }
  };

  if (!user) {
    return <div>Loading...</div>;
  }

  const showConversationList = isMobile ? !activeConversationId : true;
  const showChatView = isMobile ? !!activeConversationId : true;

  return (
    <div>
      <ChatContainer $isTablet={isTablet} $isMobile={isMobile}>
        <Row style={{ height: '100%' }}>
          {showConversationList && (
            <Col
              xs={24}
              sm={24}
              md={8}
              lg={8}
              xl={6}
              style={{ height: '100%', display: showConversationList ? 'block' : 'none' }}
            >
              <ConversationList
                conversations={conversations}
                activeConversationId={activeConversationId}
                currentUserId={user.uid}
                isLoading={isLoading}
                onSelectConversation={handleSelectConversation}
                onNewChat={() => setNewChatModalVisible(true)}
                onMute={muteConversation}
                onArchive={archiveConversation}
                onPin={pinConversation}
                onOpenSearch={() => setSearchModalVisible(true)}
              />
            </Col>
          )}
          {showChatView && (
            <Col
              xs={24}
              sm={24}
              md={16}
              lg={16}
              xl={18}
              style={{ height: '100%', display: showChatView ? 'block' : 'none' }}
            >
              <ChatView
                conversation={activeConversation}
                messages={activeMessages}
                currentUserId={user.uid}
                isLoading={isLoading}
                onSendMessage={handleSendMessage}
                onTyping={handleTyping}
                scope={SCOPE}
                municipalityId={MUNICIPALITY_ID}
                onBack={isMobile ? handleBackToConversations : undefined}
              />
            </Col>
          )}
        </Row>
      </ChatContainer>
      <NewChatModal
        visible={newChatModalVisible}
        currentUserId={user.uid}
        scope={SCOPE}
        municipalityId={MUNICIPALITY_ID}
        onClose={() => setNewChatModalVisible(false)}
        onCreateDirectChat={handleCreateDirectChat}
        onCreateGroupChat={handleCreateGroupChat}
      />
      <AdvancedSearchModal
        open={searchModalVisible}
        onClose={() => setSearchModalVisible(false)}
        currentUserId={user.uid}
        scope={SCOPE}
        municipalityId={MUNICIPALITY_ID}
        conversations={conversations}
        onJumpToMessage={handleJumpToMessage}
      />
    </div>
  );
};

export default Chat;
